// src/FileManager.js
// Enhanced version for breadcrumbs and user/admin roles.
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './FileManager.css'; // Import the CSS
//import './all.css';
import webdavService from '../services/webdavService';
import JSZip from 'jszip';
import CachedGridThumbnail from './CachedGridThumbnail';
import { getRuntimeConfig } from '../config';

// --- Helper Functions ---

const formatBytes = (bytes, decimals = 1) => {
  if (bytes == null || isNaN(Number(bytes))) return '--';
  const numBytes = Number(bytes);
  if (numBytes === 0) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(numBytes) / Math.log(k));
  return parseFloat((numBytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  if (!dateString) return '--';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch (e) {
    console.error("Error formatting date:", dateString, e);
    return '--';
  }
};

// --- File Type Validation ---

const getAllowedFileTypes = () => {
  return {
    // Video formats (most famous)
    video: ['mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm', 'flv', 'mpeg', 'mpg', 'm4v', '3gp', 'ogv'],

    // Audio formats (most famous)
    audio: ['mp3', 'wav', 'ogg', 'aac', 'flac', 'wma', 'm4a', 'opus'],

    // Image formats (most famous)
    image: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tif', 'tiff', 'ico'],

    // Document formats
    pdf: ['pdf'],
    html: ['html', 'htm'],
    excel: ['xls', 'xlsx', 'csv'],
    text: ['txt'],
    // NEW: URL files
    url: ['url']
  };
};

const getAllowedExtensions = () => {
  const types = getAllowedFileTypes();
  return [
    ...types.video,
    ...types.audio,
    ...types.image,
    ...types.pdf,
    ...types.html,
    ...types.excel,
    ...types.text,
    ...types.url // Include URL files in allowed extensions
  ];
};

const isFileTypeAllowed = (filename) => {
  if (!filename) return false;
  
  const extension = filename.split('.').pop().toLowerCase();
  const allowedExtensions = getAllowedExtensions();
  
  return allowedExtensions.includes(extension);
};

const getFileTypeCategory = (filename) => {
  if (!filename) return 'unknown';

  const extension = filename.split('.').pop().toLowerCase();
  const types = getAllowedFileTypes();

  if (types.video.includes(extension)) return 'video';
  if (types.audio.includes(extension)) return 'audio';
  if (types.image.includes(extension)) return 'image';
  if (types.pdf.includes(extension)) return 'pdf';
  if (types.html.includes(extension)) return 'html';
  if (types.excel.includes(extension)) return 'excel';
  if (types.text.includes(extension)) return 'text';
  if (types.url.includes(extension)) return 'url'; // Recognize URL type

  return 'unknown';
};

const getNotAllowedFilesMessage = (rejectedFiles) => {
  if (!rejectedFiles || rejectedFiles.length === 0) return '';

  const fileList = rejectedFiles.map(f => f.name).join(', ');
  const allowedTypes = getAllowedFileTypes();

  return `❌ The following files are not allowed: ${fileList}\n\n✅ Allowed file types:\n` +
         `🎥 Video: ${allowedTypes.video.join(', ')}\n` +
         `🎵 Audio: ${allowedTypes.audio.join(', ')}\n` +
         `🖼️ Images: ${allowedTypes.image.join(', ')}\n` +
         `📄 Documents: ${allowedTypes.pdf.join(', ')}, ${allowedTypes.html.join(', ')}, ${allowedTypes.excel.join(', ')}, ${allowedTypes.text.join(', ')}\n` +
         `🔗 URLs: ${allowedTypes.url.join(', ')}`; // Add URL files
};

const getFileType = (filename) => {
  if (!filename) return 'generic';
  const extension = filename.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tif', 'tiff'].includes(extension)) return 'image';
  if (['mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm', 'flv', 'mpeg', 'mpg', 'm4v'].includes(extension)) return 'video';
  if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'mid', 'midi'].includes(extension)) return 'audio';
  if (['pdf'].includes(extension)) return 'pdf';
  if (['doc', 'docx', 'rtf'].includes(extension)) return 'document';
  if (['xls', 'xlsx', 'csv'].includes(extension)) return 'spreadsheet';
  if (['ppt', 'pptx'].includes(extension)) return 'presentation';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) return 'archive';
  if (['txt', 'md', 'log'].includes(extension)) return 'text';
  if (['js', 'jsx', 'ts', 'tsx', 'html', 'css', 'json', 'py', 'java', 'c', 'cpp'].includes(extension)) return 'code';
  if (['url'].includes(extension)) return 'url'; // Recognize .url files
  return 'generic';
};

// --- Icon Components ---
const FolderIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>;
const FileIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path><polyline points="13 2 13 9 20 9"></polyline></svg>;
const ImageIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4caf50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>;
const VideoIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f44336" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"></polygon><rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect></svg>;
const AudioIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9c27b0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"></path><circle cx="6" cy="18" r="3"></circle><circle cx="18" cy="16" r="3"></circle></svg>;
const PdfIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ff5722" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>;
const DocumentIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ff9800" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>;
const SpreadsheetIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4caf50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><line x1="10" y1="9" x2="8" y2="9"></line><line x1="10" y1="13" x2="10" y2="17"></line><line x1="14" y1="9" x2="14" y2="17"></line></svg>;
const PresentationIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ff9800" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="12" y1="18" x2="12" y2="12"></line><line x1="9" y1="15" x2="15" y2="15"></line></svg>;
const ArchiveIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#607d8b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>;
const TextIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>;
const CodeIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#03a9f4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg>;
const UpArrowIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"></line><polyline points="5 12 12 5 19 12"></polyline></svg>;
const RefreshIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>;
const NewFolderIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 4h14a2 2 0 0 1 2 2v6H3V6a2 2 0 0 1 2-2z"></path><path d="M3 12h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-8z"></path><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>;
const UploadIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>;
const DownloadIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>;
const GridIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect></svg>;
const ListIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>;
const SearchIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>;
const ChevronRightIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>;
const SortAscIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 6l-6 6h12l-6-6z" transform="scale(1, -1) translate(0, -24)"/></svg>;
const SortDescIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 6l-6 6h12l-6-6z"/></svg>;
const PasteIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect></svg>;
const CopyIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>;
const RenameIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>;
const CutIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="3"></circle><circle cx="6" cy="18" r="3"></circle><line x1="20" y1="4" x2="8.12" y2="15.88"></line><line x1="14.47" y1="14.48" x2="20" y2="20"></line><line x1="8.12" y1="8.12" x2="12" y2="12"></line></svg>;
const DeleteIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>;
const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;
const ChevronLeftIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>;
// New Home Icon
const HomeIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>;
const SelectAllIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>;


// Helper function to extract device name from path
const getDeviceNameFromPath = (path) => {
  console.log('🔍 getDeviceNameFromPath input:', path);
  
  // Path format: /home/username/devicename/... or /devicename/...
  const pathParts = path.split('/').filter(Boolean);
  console.log('🔍 pathParts:', pathParts);
  
  if (pathParts.length >= 3 && pathParts[0] === 'home') {
    // /home/username/devicename/file.mp4 -> devicename
    const deviceName = pathParts[2];
    console.log('🔍 Found device name (home path):', deviceName);
    return deviceName;
  } else if (pathParts.length >= 1) {
    // /devicename/file.mp4 -> devicename (fallback)
    const deviceName = pathParts[0];
    console.log('🔍 Found device name (fallback):', deviceName);
    return deviceName;
  }
  
  console.log('🔍 No device name found, returning null');
  return null;
};

// --- Main Component ---
function FileManager(props) {
  const {
  initialPath = '',
  isEmbedded = false,
  customBaseUrl = null,
  showTitle = '',
  maxRows = 5, // Default for embedded, though not strictly used for row limiting now
  selectedDevice, // Added from App.js for context
  deviceName, // Device name for embedded thumbnails
} = props;
const getIconComponent = useCallback((item, requestThumbnail = false, thumbSize = 'medium') => {
    if (item.isDirectory) {
      return <FolderIcon />;
    }

    const fileType = getFileType(item.name);

    // Use CachedGridThumbnail for images, videos, and PDFs when requested
    if (requestThumbnail && (fileType === 'image' || fileType === 'video' || fileType === 'pdf')) {
      return (
        <CachedGridThumbnail
          itemPath={item.path}
          isVideo={fileType === 'video'}
          isPdf={fileType === 'pdf'}
          deviceName={isEmbedded ? deviceName : ""}
          size={thumbSize}
          lazyLoad={thumbSize === 'list'} // lazy-load in list view (many rows); eager in grid
          isFileManagerContext={true}
          isEmbedded={isEmbedded}
          key={item.path}
        />
      );
    }

    // Return appropriate icon based on file type (fallback)
    switch (fileType) {
      case 'image': return <ImageIcon />;
      case 'video': return <VideoIcon />;
      case 'audio': return <AudioIcon />;
      case 'pdf': return <PdfIcon />;
      case 'document': return <DocumentIcon />;
      case 'spreadsheet': return <SpreadsheetIcon />;
      case 'presentation': return <PresentationIcon />;
      case 'archive': return <ArchiveIcon />;
      case 'text': return <TextIcon />;
      case 'code': return <CodeIcon />;
      default: return <FileIcon />;
    }
  }, [isEmbedded, deviceName]); // Add dependencies

  const [user, setUser] = useState(null);
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [contextMenuSelection, setContextMenuSelection] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('fm_sortBy') || 'name');
  const [sortDirection, setSortDirection] = useState(() => localStorage.getItem('fm_sortDirection') || 'asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateFolderDialog, setShowCreateFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [newName, setNewName] = useState('');
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [contextMenuTarget, setContextMenuTarget] = useState(null); // Can be an item or null for empty space
  const [contextMenuType, setContextMenuType] = useState('item'); // 'item' or 'empty-space'
  const [showUploadProgress, setShowUploadProgress] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [currentDownloadFileName, setCurrentDownloadFileName] = useState('');
  const [totalFilesToUpload, setTotalFilesToUpload] = useState(0);
  const [uploadedFilesCount, setUploadedFilesCount] = useState(0);
  const [currentUploadFileName, setCurrentUploadFileName] = useState('');
  const [clipboard, setClipboard] = useState({ items: [], operation: null });
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewItems, setPreviewItems] = useState([]);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const contextMenuRef = useRef(null);
  const fileManagerRef = useRef(null);
  const listHeaderRef = useRef(null);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [pendingWarningAction, setPendingWarningAction] = useState(null);
  
  // New state for mouse drag selection
  const [isDraggingSelection, setIsDraggingSelection] = useState(false);
  const [selectionStartPos, setSelectionStartPos] = useState({ x: 0, y: 0 });
  const [selectionEndPos, setSelectionEndPos] = useState({ x: 0, y: 0 });
  
  // Drag and Drop for moving files state
  const [draggedItems, setDraggedItems] = useState([]);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const dragMoveCounter = useRef(0);
  const [successMessage, setSuccessMessage] = useState(null);
  const longPressTimer = useRef(null);

  const isAdmin = useMemo(() => user?.is_admin, [user]);
  // Staff (brand_manager / branch_manager / operator) browse their PARENT
  // owner's folder, not their own. Returns null if the role is staff but
  // parent_username hasn't arrived yet (cached user from localStorage may
  // lack it until /api/auth/profile resolves) — the listing effect skips
  // until we know which path to hit.
  const username = useMemo(() => {
    if (!user) return null;
    const role = user.role || (user.is_admin ? 'admin' : 'owner');
    if (['brand_manager','branch_manager','operator'].includes(role)) {
      return user.parent_username || null;
    }
    return user.username;
  }, [user]);

  // Device paths relative to user home, from media_files_link
  // e.g. ['domiat/domiat-1', 'gamasa/gamasa-1', 'vxi-01']
  // null = not yet fetched, [] = fetched but empty
  const [devicePaths, setDevicePaths] = useState(null);

  useEffect(() => {
    if (!user || user.is_admin) return; // admins see everything
    const token = localStorage.getItem('token');
    fetch('/api/user/file-manager-folders', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : {})
      .then(data => setDevicePaths(Array.isArray(data.devicePaths) ? data.devicePaths : []))
      .catch(() => setDevicePaths([]));
  }, [user]);

  // Helper function to show NOT_ALLOWED_FOLDER popup
  const showNotAllowedPopup = (message) => {
    const modal = document.createElement('div');
    modal.className = 'fm-dialog-overlay';
    modal.innerHTML = `
      <div class="fm-dialog">
        <h3 style="color: #ff6b6b;">⚠️ Operation Not Allowed</h3>
        <p>${message || 'You can only perform this operation inside device folders, not at the user home level.'}</p>
        <div class="fm-dialog-buttons">
          <button class="fm-button-primary" id="closeNotAllowedPopup">OK</button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);

    document.getElementById('closeNotAllowedPopup').onclick = () => {
      document.body.removeChild(modal);
    };
  };

  // Touch event handlers for long press
  const handleTouchStart = (e, item) => {
      // Prevent default context menu on long press
      e.preventDefault();
      
      longPressTimer.current = setTimeout(() => {
          // Trigger context menu after a long press
          handleContextMenu(e, item);
      }, 500); // 500ms for a long press
  };

  const handleTouchMove = (e) => {
      // If the user moves too much, cancel the long press
      if (longPressTimer.current) {
          clearTimeout(longPressTimer.current);
          longPressTimer.current = null;
      }
      setTouchEndX(e.touches[0].clientX);
  };
  
  const handleTouchEnd = (e, item) => {
      if (longPressTimer.current) {
          clearTimeout(longPressTimer.current);
          longPressTimer.current = null;
          
          // If no significant movement, treat as a regular tap
          if (touchStartX === touchEndX || !touchEndX) {
              handleItemClick(item, e);
          }
      }
      setTouchStartX(null);
      setTouchEndX(null);
  };

  const handleDragSelectionStart = (e) => {
    // Only start if it's a left click and not on an item or toolbar
    if (e.button !== 0 || e.target.closest('.fm-grid-item, .fm-list-row, .fm-toolbar, .fm-breadcrumbs')) {
      return;
    }
    e.preventDefault();
    setIsDraggingSelection(true);
    setSelectionStartPos({ x: e.clientX, y: e.clientY });
    setSelectionEndPos({ x: e.clientX, y: e.clientY });
    setSelectedItems([]); // Start with a fresh selection
  };

  const handleDragSelectionMove = (e) => {
    if (!isDraggingSelection) return;
    e.preventDefault();
    setSelectionEndPos({ x: e.clientX, y: e.clientY });
    updateSelectionFromDrag();
  };

  const handleDragSelectionEnd = () => {
    if (!isDraggingSelection) return;
    setIsDraggingSelection(false);
    setSelectionStartPos({ x: 0, y: 0 });
    setSelectionEndPos({ x: 0, y: 0 });
  };
  
  const updateSelectionFromDrag = () => {
    if (!fileManagerRef.current) return;

    const filesInView = Array.from(fileManagerRef.current.querySelectorAll('.fm-grid-item, .fm-list-row'));
    const newSelectedItems = [];

    const selectionRect = {
      x: Math.min(selectionStartPos.x, selectionEndPos.x),
      y: Math.min(selectionStartPos.y, selectionEndPos.y),
      width: Math.abs(selectionStartPos.x - selectionEndPos.x),
      height: Math.abs(selectionStartPos.y - selectionEndPos.y),
    };
    
    filesInView.forEach(itemElement => {
      const itemRect = itemElement.getBoundingClientRect();
      
      const itemPath = itemElement.dataset.path;
      if (!itemPath) return;

      // Check for intersection
      if (
        itemRect.x < selectionRect.x + selectionRect.width &&
        itemRect.x + itemRect.width > selectionRect.x &&
        itemRect.y < selectionRect.y + selectionRect.height &&
        itemRect.y + itemRect.height > selectionRect.y
      ) {
        newSelectedItems.push(itemPath);
      }
    });

    setSelectedItems(newSelectedItems);
  };


  const fetchFiles = useCallback(async (path, forceRefresh = false) => {
    // Wait for username resolution. Staff users get null until /api/auth/profile
    // returns parent_username; firing a fetch with null would land on /home/null
    // (or fall through to '/' which fails the /home/ prefix check) and render
    // the empty state.
    if (!username && !isAdmin && !path) {
      setLoading(true);
      return;
    }
    const authSuccess = await webdavService.ensureAuthenticated();
    if (!authSuccess) {
      console.error('Authentication failed, cannot fetch files.');
      setError("Authentication failed. Please log out and back in.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    setSelectedItems([]);

    let targetPath = path;
    if (!targetPath && username) {
        targetPath = isAdmin ? '/home/' : `/home/${username}/`;
    } else if (!targetPath) { 
        targetPath = initialPath || '/';
    }

    if (!targetPath.endsWith('/')) targetPath += '/';
    
    // Defensive check to ensure path is valid
    if (!targetPath.startsWith('/home/')) {
        console.error(`Attempted to navigate to an invalid path: ${targetPath}`);
        setError("Invalid path provided. Navigation blocked.");
        setLoading(false);
        return;
    }

    console.log('Fetching files for path:', targetPath, 'User:', username, 'IsAdmin:', isAdmin, 'ForceRefresh:', forceRefresh);

    try {
      const response = await webdavService.getDirectoryListing(targetPath, webdavService.getBaseUrl(), forceRefresh);
      setFiles(response.items || []);
      setCurrentPath(response.path || targetPath);
      updateBreadcrumbs(response.path || targetPath);
      console.log(`✅ Fetched ${response.items.length} items from ${response.path}`);
    } catch (err) {
      console.error('Error fetching files:', err);
      // More user-friendly error messages based on the error object
      const errorMessage = err.message || 'Unknown error occurred.';
      setError(`Failed to fetch files: ${errorMessage}`);
      setFiles([]);
      updateBreadcrumbs(targetPath);
    } finally {
      setLoading(false);
    }
  }, [username, isAdmin, initialPath, webdavService]);

  const updateBreadcrumbs = useCallback((path) => {
    const crumbs = [];
    const pathSegments = path.split('/').filter(Boolean); // e.g., ['home', 'user', 'folder']

    if (isAdmin) {
        // Admin: "Homes" -> "user" -> "subfolder"
        crumbs.push({ name: 'Homes', path: '/home/' }); // Admin always sees "Homes" as root
        if (pathSegments[0] === 'home' && pathSegments.length > 1) {
            let currentBuiltPath = '/home/';
            for (let i = 1; i < pathSegments.length; i++) {
                currentBuiltPath += `${pathSegments[i]}/`;
                crumbs.push({ name: decodeURIComponent(pathSegments[i]), path: currentBuiltPath });
            }
        }
    } else if (username) {
        // Regular user: "Home" (their root) -> "subfolder"
        const userHomePath = `/home/${username}/`;
        crumbs.push({ name: 'Home', path: userHomePath });

        if (path.startsWith(userHomePath) && path !== userHomePath) {
            const relativePath = path.substring(userHomePath.length);
            const userSubSegments = relativePath.split('/').filter(Boolean);
            let currentBuiltPath = userHomePath;
            userSubSegments.forEach(segment => {
                currentBuiltPath += `${segment}/`;
                crumbs.push({ name: decodeURIComponent(segment), path: currentBuiltPath });
            });
        }
    }
    // If path is just '/home/' for admin, or userHomeRootPath for user, crumbs already has the base.
    // If path is just '/' (should resolve to /home/ for admin or /home/user/ for user), it's also handled.

    setBreadcrumbs(crumbs);
  }, [isAdmin, username]);


  useEffect(() => {
    if (customBaseUrl) webdavService.setCustomBaseUrl(customBaseUrl);
    const storedUser = localStorage.getItem('user');
    
    const initializeFileManager = async (userData) => {
        setUser(userData);
        webdavService.username = userData.username;
        
        let startingPath = initialPath;
        if (!startingPath) {
            if (userData.is_admin) {
                startingPath = '/home/';
            } else {
                // Staff (brand_manager / branch_manager / operator) browse their
                // PARENT owner's folder, not their own (managers don't own files).
                const role = userData.role || (userData.is_admin ? 'admin' : 'owner');
                const isStaff = ['brand_manager','branch_manager','operator'].includes(role);
                const homeOwner = (isStaff && userData.parent_username) ? userData.parent_username : userData.username;
                if (homeOwner) {
                    startingPath = `/home/${homeOwner}/`;
                } else {
                    startingPath = '/';
                }
            }
        }
        
        console.log(`FileManager Init: Role='${userData.is_admin ? 'admin' : 'user'}', User='${userData.username}', isEmbedded=${isEmbedded}, propInitialPath='${initialPath}', customBaseUrl='${customBaseUrl}', calculated startingPath='${startingPath}'`);
        
        await fetchFiles(startingPath);
    };

    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        initializeFileManager(userData);
      } catch (e) {
        setError('Failed to load user data from storage.');
        setLoading(false);
      }
    } else {
      setError('No user logged in.');
      setLoading(false);
    }
    
    return () => webdavService.resetBaseUrl();
  }, [initialPath, customBaseUrl, fetchFiles]);

  useEffect(() => {
  if (isEmbedded && viewMode !== 'grid') setViewMode('grid');
}, [isEmbedded, viewMode]);

  useEffect(() => localStorage.setItem('fm_sortBy', sortBy), [sortBy]);
  useEffect(() => localStorage.setItem('fm_sortDirection', sortDirection), [sortDirection]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) setShowContextMenu(false);
      if (showPreviewModal && event.target.classList.contains('fm-preview-modal-overlay')) handleClosePreviewModal();
    };
    if (showContextMenu || showPreviewModal) document.addEventListener('mousedown', handleClickOutside);
    else document.removeEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showContextMenu, showPreviewModal]); // Removed handleClosePreviewModal from deps

  useEffect(() => {
    if (showPreviewModal && previewItems.length > 0) loadPreviewContent(currentPreviewIndex);
    return () => {
      if (previewBlobUrl) { URL.revokeObjectURL(previewBlobUrl); setPreviewBlobUrl(null); }
    };
  }, [showPreviewModal, currentPreviewIndex, previewItems]); // Removed loadPreviewContent from deps

  const sortItems = useCallback((items, field, direction) => {
    return [...items].sort((a, b) => {
      if (a.isDirectory && !b.isDirectory) return -1;
      if (!a.isDirectory && b.isDirectory) return 1;
      let comparison = 0;
      let valA, valB;
      switch (field) {
        case 'name':
          valA = a.name || ''; valB = b.name || '';
          comparison = valA.localeCompare(valB, undefined, { sensitivity: 'base', numeric: true });
          break;
        case 'size':
          valA = a.isDirectory ? -1 : (a.size || 0); valB = b.isDirectory ? -1 : (b.size || 0);
          comparison = valA - valB;
          break;
        case 'modified':
          valA = a.modified ? new Date(a.modified).getTime() : 0; valB = b.modified ? new Date(b.modified).getTime() : 0;
          comparison = valA - valB;
          break;
        default: comparison = 0;
      }
      return direction === 'asc' ? comparison : -comparison;
    });
  }, []);

  const handleNavigate = (path) => {
    setShowContextMenu(false);
    fetchFiles(path);
  };

  const handleUpDirectory = () => {
    if (loading) return;
    let targetParentPath = '/'; // Default fallback

    if (isAdmin) {
        // Admin can navigate up until '/home/'
        if (currentPath !== '/home/' && currentPath.startsWith('/home/')) {
            const segments = currentPath.split('/').filter(Boolean); // "home", "user", "folder"
            if (segments.length > 1) { // More than just "home"
                 targetParentPath = `/${segments.slice(0, -1).join('/')}/`;
            } else { // currentPath is /home/something/
                 targetParentPath = '/home/';
            }
        } else {
            return; // Admin is at /home/ or above, cannot go "up" further in this UI context
        }
    } else if (username) {
        const userHomePath = `/home/${username}/`;
        if (currentPath !== userHomePath && currentPath.startsWith(userHomePath)) {
            const segments = currentPath.substring(userHomePath.length).split('/').filter(Boolean);
            if (segments.length > 1) {
                // Go to parent — upDisabled already blocks at branch level
                targetParentPath = `${userHomePath}${segments.slice(0, -1).join('/')}/`;
            } else if (segments.length === 1) {
                targetParentPath = userHomePath;
            }
        } else {
            return; // Already at home root
        }
    }
    if (targetParentPath) {
        handleNavigate(targetParentPath);
    }
};


  const handleRefresh = () => {
    if (!loading) { // Prevent multiple refresh clicks while one is in progress
       fetchFiles(currentPath, true); // Pass true to force refresh
    }
};
  const handleSort = (field) => {
    let newDirection = 'asc';
    if (sortBy === field) newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    setSortBy(field); setSortDirection(newDirection);
  };

  const handleItemClick = (item, event) => {
    if (event.target.type === 'checkbox' || event.target.closest('.fm-grid-item-checkbox')) {
        // Clicks on checkbox handled by onChange
        return;
    }
    if (item.isDirectory) {
        handleNavigate(item.path);
    } else {
        // If Ctrl or Cmd is pressed, toggle selection without navigating
        if (event.ctrlKey || event.metaKey) {
            handleItemSelect(item.path, { target: { checked: !selectedItems.includes(item.path) } });
        } else {
            // A regular click on an item that isn't already part of a multi-selection
            if (selectedItems.length <= 1) {
                 handlePreview(item);
            } else {
                // Clicking on a new item clears the existing selection
                setSelectedItems([item.path]);
            }
        }
    }
};


  const handleItemSelect = (itemPath, event) => {
    const isChecked = event.target.checked;
    setSelectedItems(prev => isChecked ? [...prev, itemPath] : prev.filter(path => path !== itemPath));
  };

  const handleSelectAll = (event) => {
    // If event is defined, it's from a checkbox. Check its `checked` property.
    // If event is undefined (e.g., from context menu), it means "select all".
    const isChecked = event ? event.target.checked : true; 
    setSelectedItems(isChecked ? filteredAndSortedFiles.map(item => item.path) : []);
  };

  const handleSearchChange = (event) => setSearchTerm(event.target.value);
  const handleViewModeChange = (mode) => setViewMode(mode);

  const handleCreateFolder = async () => {
  if (!newFolderName || newFolderName.includes('/')) {
    alert(newFolderName.includes('/') ? 'Folder name cannot contain slashes.' : 'Folder name cannot be empty.');
    return;
  }
  const path = `${currentPath}${newFolderName}/`;
  setLoading(true); setError(null);
  try {
    const result = await webdavService.createDirectory(path);
    if (result.success) {
      setShowCreateFolderDialog(false); 
      setNewFolderName(''); 
      // CRITICAL FIX: Auto-refresh without blocking
      fetchFiles(currentPath, true);
      console.log(`✅ Folder "${newFolderName}" created and view refreshed`);
    } else { 
      // Use alert for user-facing messages
      alert(`Failed to create folder: ${result.message || 'Unknown error'}`); 
      setError(`Failed to create folder: ${result.message || 'Unknown error'}`); 
    }
  } catch (err) { 
    alert(`Failed to create folder: ${err.message || 'Unknown error'}`);
    setError(`Failed to create folder: ${err.message || 'Unknown error'}`); 
  } finally { 
    setLoading(false); 
  }
};

  const handleRename = async () => {
    if (!newName || !renameTarget || newName.includes('/')) {
      alert(newName.includes('/') ? 'Name cannot contain slashes.' : 'New name cannot be empty.');
      return;
    }
    const oldPath = renameTarget.path;
    const pathSegments = oldPath.split('/').filter(Boolean);
    const parentPath = '/' + pathSegments.slice(0, -1).join('/') + '/';
    const newPath = `${parentPath}${newName}${renameTarget.isDirectory ? '/' : ''}`;
    if (oldPath === newPath) { setShowRenameDialog(false); return; }
    setLoading(true); setError(null);
    try {
      await webdavService.move(oldPath, newPath);
      setShowRenameDialog(false); setNewName(''); setRenameTarget(null); 
      // CRITICAL FIX: Auto-refresh without blocking
      fetchFiles(currentPath, true);
    } catch (err) { 
      // Check if it's a NOT_ALLOWED_FOLDER error
      if (err.code === 'NOT_ALLOWED_FOLDER') {
        showNotAllowedPopup(err.message);
      } else {
        alert(`Failed to rename: ${err.message || 'Unknown error'}`);
        setError(`Failed to rename: ${err.message || 'Unknown error'}`); 
      }
    }
    finally { setLoading(false); }
  };

  const handleDeleteSelected = () => {
    if (selectedItems.length === 0) return;
    const itemsToDelete = filteredAndSortedFiles.filter(item => selectedItems.includes(item.path));
    handleDelete(itemsToDelete.map(i => i.path));
  };

  const handleDelete = async (itemsToDeletePaths) => {
  if (!itemsToDeletePaths || itemsToDeletePaths.length === 0) return;
  
  // Use a custom modal / confirmation dialog instead of window.confirm
  const confirmDelete = await new Promise(resolve => {
    const modal = document.createElement('div');
    modal.className = 'fm-dialog-overlay';
    modal.innerHTML = `
      <div class="fm-dialog">
        <h3>Confirm Deletion</h3>
        <p>Are you sure you want to delete ${itemsToDeletePaths.length} item(s)? This cannot be undone.</p>
        <div class="fm-dialog-buttons">
          <button class="fm-button-secondary" id="cancelDelete">Cancel</button>
          <button class="fm-button-primary danger" id="confirmDelete">Delete</button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);

    document.getElementById('cancelDelete').onclick = () => {
      document.body.removeChild(modal);
      resolve(false);
    };
    document.getElementById('confirmDelete').onclick = () => {
      document.body.removeChild(modal);
      resolve(true);
    };
  });

  if (!confirmDelete) return;
  
  setLoading(true); 
  setError(null);
  let failedDeletes = 0; 
  let errorMessages = [];
  
  try {
    for (const path of itemsToDeletePaths) {
      try { 
        await webdavService.delete(path); 
      } catch (singleErr) {
        // Check if it's a NOT_ALLOWED_FOLDER error
        if (singleErr.code === 'NOT_ALLOWED_FOLDER') {
          showNotAllowedPopup(singleErr.message);
          setLoading(false);
          setSelectedItems([]);
          setShowContextMenu(false);
          return; // Stop deletion process
        }
        failedDeletes++; 
        errorMessages.push(`Failed to delete ${path.split('/').pop()}: ${singleErr.message}`);
      }
    }
    
    // CRITICAL FIX: Auto-refresh without blocking
    fetchFiles(currentPath, true); 
    
    if (failedDeletes > 0) alert(`Deletion completed with errors: ${errorMessages.join('; ')}`);
  } catch (err) { 
    alert(`An unexpected error occurred during deletion: ${err.message}`);
    setError(`An unexpected error occurred during deletion: ${err.message}`); 
  } finally { 
    setLoading(false); 
    setSelectedItems([]); 
    setShowContextMenu(false); 
  }
};

  const handleUploadClick = () => fileInputRef.current?.click();
  const handleFolderUploadClick = () => folderInputRef.current?.click();

const uploadItems = async (items) => {
  if (!items || items.length === 0) return;
  
  // Validate file types BEFORE starting upload
  let allFiles = [];
  let rejectedFiles = [];
  
  const processEntry = async (entry, currentUploadPath) => {
    if (entry.isFile) {
      return new Promise((resolve, reject) => 
        entry.file(file => { 
          // Check if file type is allowed
          if (isFileTypeAllowed(file.name)) {
            allFiles.push({ file: file, path: `${currentUploadPath}${file.name}` });
          } else {
            rejectedFiles.push(file);
          }
          resolve(); 
        }, reject)
      );
    } else if (entry.isDirectory) {
      const dirPath = `${currentUploadPath}${entry.name}/`;
      try { 
        await webdavService.createDirectory(dirPath); 
      } catch (dirErr) { 
        if (dirErr.response?.status !== 405) { 
          console.warn(`Failed to create directory ${entry.name}: ${dirErr.message}`); 
          return; 
        } 
      }
      const reader = entry.createReader();
      return new Promise((resolve, reject) => {
        const readEntries = async () => {
          reader.readEntries(async (entries) => {
            if (entries.length > 0) { 
              for (const subEntry of entries) await processEntry(subEntry, dirPath); 
              readEntries(); 
            } else resolve();
          }, reject);
        }; 
        readEntries();
      });
    }
  };

  if (items instanceof FileList) {
    let promises = [];
    uploadItems.createdDirs = new Set();
    
    for (let i = 0; i < items.length; i++) {
      const file = items[i];
      
      // Check if file type is allowed
      if (isFileTypeAllowed(file.name)) {
        const relativePath = file.webkitRelativePath || file.name;
        const targetPath = `${currentPath}${relativePath}`;
        
        if (file.webkitRelativePath) {
          const pathSegments = relativePath.split('/');
          let cumulativePath = currentPath;
          for (let j = 0; j < pathSegments.length - 1; j++) {
            cumulativePath += `${pathSegments[j]}/`;
            if (!uploadItems.createdDirs.has(cumulativePath)) {
              promises.push(webdavService.createDirectory(cumulativePath).catch(dirErr => { 
                if (dirErr.response?.status !== 405) throw dirErr; 
              }).finally(() => uploadItems.createdDirs.add(cumulativePath)));
            }
          }
        }
        promises.push(Promise.resolve().then(() => allFiles.push({ file: file, path: targetPath })));
      } else {
        rejectedFiles.push(file);
      }
    }
    
    try { 
      await Promise.all(promises); 
    } catch (setupErr) { 
      console.error('Setup error:', setupErr);
    }
    delete uploadItems.createdDirs;
    
  } else if (items instanceof DataTransferItemList) {
    await Promise.all(Array.from(items).filter(item => item.kind === 'file').map(item => processEntry(item.webkitGetAsEntry(), currentPath)));
  } else { 
    alert("Unsupported item type for upload."); 
    setError("Unsupported item type for upload."); 
    return; 
  }

  // Show rejected files message if any
  if (rejectedFiles.length > 0) {
    const message = getNotAllowedFilesMessage(rejectedFiles);
    
    // Use custom alert for rejected files
    const continueUpload = await new Promise(resolve => {
      const modal = document.createElement('div');
      modal.className = 'fm-dialog-overlay';
      modal.innerHTML = `
        <div class="fm-dialog">
          <h3>File Type Warning</h3>
          <p>${message}</p>
          ${allFiles.length > 0 ? `<p>Do you want to continue uploading the ${allFiles.length} allowed file(s)?</p>` : ''}
          <div class="fm-dialog-buttons">
            <button class="fm-button-secondary" id="cancelUpload">Cancel</button>
            ${allFiles.length > 0 ? '<button class="fm-button-primary" id="continueUpload">Continue Upload</button>' : ''}
          </div>
        </div>
      `;
      document.body.appendChild(modal);

      document.getElementById('cancelUpload').onclick = () => {
        document.body.removeChild(modal);
        resolve(false);
      };
      if (allFiles.length > 0) {
        document.getElementById('continueUpload').onclick = () => {
          document.body.removeChild(modal);
          resolve(true);
        };
      }
    });
    
    if (!continueUpload) {
      console.log('⏹️ User cancelled upload');
      return;
    }
  }

  // Continue with upload for allowed files only
  const filesToUpload = allFiles;
  
  if (filesToUpload.length === 0) {
    console.log('📂 No files to upload');
    return;
  }
  
  console.log(`📤 Starting upload of ${filesToUpload.length} allowed files`);
  
  setTotalFilesToUpload(filesToUpload.length); 
  setUploadedFilesCount(0); 
  setUploadProgress(0);
  setShowUploadProgress(true); 
  setError(null);
  let uploadErrors = [];

  for (let i = 0; i < filesToUpload.length; i++) {
    const { file, path: targetPath } = filesToUpload[i];
    
    // Reset progress for this file
    setUploadProgress(0);
    setCurrentUploadFileName(file.name); 
    setUploadedFilesCount(i);
    
    const fileType = getFileTypeCategory(file.name);
    console.log(`📤 Uploading ${fileType} file ${i + 1}/${filesToUpload.length}: ${file.name} (${formatBytes(file.size)})`);
    
    try {
      await webdavService.uploadFile(targetPath, file, (fileProgress) => {
        // Calculate overall progress based on completed files + current file's progress
        const completedFilesProgress = (i / filesToUpload.length) * 100;
        const currentFileProgress = fileProgress / filesToUpload.length;
        const totalProgress = Math.round(completedFilesProgress + currentFileProgress);

        // Update progress bar and file name directly
        setUploadProgress(totalProgress);
        setCurrentUploadFileName(`${file.name} (${fileProgress}%)`);
      });
      
      console.log(`✅ Successfully uploaded ${fileType}: ${file.name}`);
      
      // Update progress to show this file is complete
      const completedProgress = Math.round(((i + 1) / filesToUpload.length) * 100);
      setUploadProgress(completedProgress);
      setCurrentUploadFileName(`✅ ${file.name}`);
      
      // Small delay to show completion
      await new Promise(resolve => setTimeout(resolve, 200));
      
    } catch (err) { 
      console.error(`❌ Upload error for ${file.name}:`, err);
      
      // Check if it's a NOT_ALLOWED_FOLDER error
      if (err.code === 'NOT_ALLOWED_FOLDER') {
        showNotAllowedPopup(err.message);
        // Stop the upload process
        break;
      }
      
      uploadErrors.push(`${file.name}: ${err.message}`);
      
      // Still update progress even on error
      const completedProgress = Math.round(((i + 1) / filesToUpload.length) * 100);
      setUploadProgress(completedProgress);
    }
  }
  
  setCurrentUploadFileName('Upload complete!'); 
  setUploadedFilesCount(filesToUpload.length); 
  setUploadProgress(100);
  
  // Hide progress after showing completion
  setTimeout(() => {
    setShowUploadProgress(false);
  }, 1500);
  
  // CRITICAL FIX: Auto-refresh without blocking
  fetchFiles(currentPath, true); 
  
  // Show summary
  const successCount = Math.max(0, filesToUpload.length - uploadErrors.length - rejectedFiles.length);
  let summaryMessage = `✅ Successfully uploaded ${successCount} file(s)`;
  if (rejectedFiles.length > 0) {
    summaryMessage += `\n⚠️ ${rejectedFiles.length} file(s) were rejected due to file type restrictions`;
  }
  if (uploadErrors.length > 0) {
    summaryMessage += `\n❌ ${uploadErrors.length} file(s) failed to upload`;
    setError(`Upload issues: ${uploadErrors.join('; ')}`);
  }
  
  // Do not show a blocking alert for success, just log it
  console.log(summaryMessage);
  
  if (fileInputRef.current) fileInputRef.current.value = '';
if (folderInputRef.current) folderInputRef.current.value = '';

// Refresh file list to show converted extensions (.mp4, .jpg)
await fetchFiles(currentPath, true);
};

  const handleFileInputChange = (event) => uploadItems(event.target.files);
  const handleFolderInputChange = (event) => uploadItems(event.target.files);
  
  // Drag and Drop for file movement
  const handleDragStart = (e, item) => {
    // Only allow drag if not multi-selecting or if item is already selected
    const itemsToDrag = selectedItems.includes(item.path) ? selectedItems : [item.path];
    e.dataTransfer.setData('text/plain', JSON.stringify(itemsToDrag));
    e.dataTransfer.effectAllowed = 'move';
    
    // Set a class to the dragged item for visual feedback
    e.target.classList.add('is-dragging');
    setTimeout(() => {
        e.target.classList.remove('is-dragging');
    }, 100);
  };
  
  const handleDropOnFolder = async (e, folderItem) => {
    e.preventDefault();
    setDragOverFolder(null); // Clear drag over state

    // Only allow move operations inside device (media) folders
    if (!canUpload) return;

    // Check if the drop target is a valid folder
    if (!folderItem || !folderItem.isDirectory) return;
    
    // Get the dragged item paths
    const draggedItemsPaths = JSON.parse(e.dataTransfer.getData('text/plain'));
    const itemsToMove = filteredAndSortedFiles.filter(item => draggedItemsPaths.includes(item.path));
    
    if (itemsToMove.length === 0) return;
    
    const destinationPath = folderItem.path;
    
    setLoading(true);
    let failedMoves = 0;
    
    for (const item of itemsToMove) {
        const newPath = `${destinationPath}${item.name}`;
        
        // Prevent moving a folder into itself
        if (item.path === destinationPath || destinationPath.startsWith(item.path)) {
            console.warn(`⚠️ Skipping move: Cannot move folder into itself.`);
            failedMoves++;
            continue;
        }
        
        try {
            await webdavService.move(item.path, newPath);
        } catch (err) {
            console.error(`❌ Failed to move ${item.name}:`, err);
            failedMoves++;
        }
    }
    
    setLoading(false);
    fetchFiles(currentPath, true); // Refresh view after operation
    
    if (failedMoves === itemsToMove.length) {
        alert(`Failed to move all selected items.`);
    } else if (failedMoves > 0) {
        alert(`Successfully moved some items, but ${failedMoves} failed.`);
    } else {
        console.log('✅ Successfully moved selected items.');
    }
  };
  
  const handleDragOverFolder = (e, folderItem) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move'; // Change cursor to 'move' icon
    setDragOverFolder(folderItem.path);
  };
  
  const handleDragLeaveFolder = (e) => {
    e.preventDefault();
    setDragOverFolder(null);
  };

  // canUpload: true when inside a device folder (or subfolder thereof), or embedded/admin
  // Uses devicePaths from DB (media_files_link) for precise checking
  const canUpload = isAdmin || isEmbedded || (() => {
    if (!username) return false;
    const userHome = `/home/${username}/`;
    if (!currentPath.startsWith(userHome)) return false;
    const currentRelPath = currentPath.substring(userHome.length).split('/').filter(Boolean).join('/');
    if (!currentRelPath) return false; // at home root
    if (devicePaths === null) return true; // still loading → permissive
    if (devicePaths.length === 0) return false;
    return devicePaths.some(p => currentRelPath === p || currentRelPath.startsWith(p + '/'));
  })();
  const handleDragEnter = (e) => { if (!canUpload) return; e.preventDefault(); e.stopPropagation(); dragCounter.current++; if (dragCounter.current === 1) setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current--; if (dragCounter.current === 0) setIsDragging(false); };
  const handleDragOver = (e) => { if (!canUpload) return; e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'copy'; };
  const handleDrop = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); dragCounter.current = 0; if (!canUpload) return; const items = e.dataTransfer.items; if (items && items.length > 0) uploadItems(items); };

  const handleDownload = async (itemsToDownload) => {
  if (!itemsToDownload || itemsToDownload.length === 0) return;
  
  setLoading(true);
  setError(null);
  setIsDownloading(true);
  setDownloadProgress(0);
  
  try {
    if (itemsToDownload.length === 1 && !itemsToDownload[0].isDirectory) {
      // Single file download
      const item = itemsToDownload[0];
      setCurrentDownloadFileName(item.name);
      
      const blob = await webdavService.downloadFile(item.path, null, {
        onProgress: (progress) => {
          setDownloadProgress(progress);
        }
      });
      
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = item.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
      
    } else {
      // Multiple files - create ZIP
      const zip = new JSZip();
      setTotalFilesToUpload(itemsToDownload.length);
      setUploadedFilesCount(0);
      
      let downloadedCount = 0;
      let errorItems = [];
      
      for (const item of itemsToDownload) {
        if (!item.isDirectory) {
          setCurrentDownloadFileName(item.name);
          try {
            const blob = await webdavService.downloadFile(item.path, null, {
              onProgress: (progress) => {
                setDownloadProgress(progress);
              }
            });
            zip.file(item.name, blob);
            downloadedCount++;
            setUploadedFilesCount(downloadedCount);
          } catch (err) {
            errorItems.push(item.name);
          }
        }
      }
      
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(zipBlob);
      link.download = `files_${new Date().toISOString().slice(0,10)}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      if (errorItems.length > 0) {
        alert(`Some items could not be downloaded: ${errorItems.join(', ')}`);
      }
    }
  } catch (err) {
    alert(`Download failed: ${err.message}`);
    setError(`Download failed: ${err.message}`);
  } finally {
    setLoading(false);
    setIsDownloading(false);
    setDownloadProgress(0);
    setCurrentDownloadFileName('');
  }
};

  const handlePreview = (item) => {
    if (item.isDirectory) return;
    const previewable = filteredAndSortedFiles.filter(f => !f.isDirectory && ['image', 'video', 'audio', 'pdf', 'text', 'document'].includes(getFileType(f.name)));
    const startIndex = previewable.findIndex(p => p.path === item.path);
    if (startIndex !== -1) { setPreviewItems(previewable); setCurrentPreviewIndex(startIndex); setShowPreviewModal(true); }
    else { alert("This file type cannot be previewed."); handleDownload([item]); }
  };

  const loadPreviewContent = async (index) => {
  if (index < 0 || index >= previewItems.length) return;
  const item = previewItems[index];
  setPreviewLoading(true);
  setPreviewError(null);

  if (previewBlobUrl) {
    URL.revokeObjectURL(previewBlobUrl);
    setPreviewBlobUrl(null);
  }

  try {
    const itemType = getFileType(item.name);

    if (itemType === 'image' || itemType === 'video') {
      console.log(`🖼️ Loading preview for: ${item.name}, isEmbedded: ${isEmbedded}`);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token');
        }
        
        let thumbnailUrl;
        const { API_BASE_URL } = getRuntimeConfig();
        
        if (isEmbedded && deviceName) {
          // Embedded case: use device-specific API (same as thumbnails)
          const filename = item.path.split('/').pop(); // Get just the filename
          thumbnailUrl = `${API_BASE_URL}/api/thumbnail/${encodeURIComponent(deviceName)}/${encodeURIComponent(filename)}`;
          console.log(`🖼️ Embedded preview API URL: ${thumbnailUrl}`);
        } else {
          // Full FileManager case: use filemanager API
          const pathParts = item.path.split('/').filter(Boolean);
          if (pathParts.length < 3 || pathParts[0] !== 'home') {
            throw new Error('Invalid path format for file manager preview');
          }
          const usernameParam = encodeURIComponent(pathParts[1]);
          const relativePathParam = encodeURIComponent(pathParts.slice(2).join('/'));
          thumbnailUrl = `${API_BASE_URL}/api/thumbnail/filemanager/${usernameParam}/${relativePathParam}`;
          console.log(`🖼️ Full FileManager preview API URL: ${thumbnailUrl}`);
        }

        const response = await fetch(thumbnailUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'image/jpeg,image/png,image/*'
          },
          credentials: 'omit',
          signal: AbortSignal.timeout(15000) // Increased timeout for previews
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPreviewBlobUrl(url);
        console.log(`✅ ${itemType} preview loaded for modal`);
        
      } catch (fetchErr) {
        console.error('❌ Preview fetch error:', fetchErr);
        setPreviewError(`Could not load preview: ${fetchErr.message}`);
      }
    } else {
      // For other files (PDF, audio, etc.), load the actual file via WebDAV
      const blob = await webdavService.fetchBlob(item.path);
      const url = URL.createObjectURL(blob);
      setPreviewBlobUrl(url);
    }

  } catch (err) {
    console.error('Preview loading error:', err);
    setPreviewError(`Could not load preview: ${err.message}`);
  } finally {
    setPreviewLoading(false);
  }
};

  const getAuthenticatedUrl = (url) => {
    if (!url) return url;
    if (webdavService.getAuthenticatedUrl) return webdavService.getAuthenticatedUrl(url);
    return url;
  };

  const handlePreviewNext = () => setCurrentPreviewIndex(prev => (prev + 1) % previewItems.length);
  const handlePreviewPrev = () => setCurrentPreviewIndex(prev => (prev - 1 + previewItems.length) % previewItems.length);
  const handleClosePreviewModal = () => { setShowPreviewModal(false); setPreviewItems([]); setCurrentPreviewIndex(0); if (previewBlobUrl) { URL.revokeObjectURL(previewBlobUrl); setPreviewBlobUrl(null); } };

  const handleContextMenu = (event, item) => {
  event.preventDefault(); 
  event.stopPropagation();
  
  setShowContextMenu(false); // Close any existing menu first
  
  setContextMenuPosition({ x: event.clientX, y: event.clientY }); 

  if (item) {
    // Clicked on an item
    setContextMenuTarget(item);
    setContextMenuType('item');
    
    // Store the current selection for context menu use
    let selectionToStore;
    if (!selectedItems.includes(item.path)) {
      // Right-clicked on unselected item
      selectionToStore = [item.path];
      setSelectedItems([item.path]);
    } else {
      // Right-clicked on selected item - keep current selection
      selectionToStore = [...selectedItems];
    }
    
    setContextMenuSelection(selectionToStore);
    console.log('Stored context menu selection:', selectionToStore);
  } else {
    // Clicked on empty space
    setContextMenuTarget(null);
    setContextMenuType('empty-space');
    setSelectedItems([]);
    setContextMenuSelection([]);
  }
  
  setTimeout(() => {
    setShowContextMenu(true);
  }, 10);
};

  const handleCloseContextMenu = () => { setShowContextMenu(false); setContextMenuTarget(null); };

  const handleCopy = (itemsToCopy) => { 
  if (!itemsToCopy || itemsToCopy.length === 0) return; 
  setClipboard({ items: itemsToCopy, operation: 'copy' }); 
  setShowContextMenu(false); 
  
  const itemNames = itemsToCopy.map(item => item.name).join(', ');
  setSuccessMessage(`✅ Copied ${itemsToCopy.length} item(s) to clipboard`);
  setTimeout(() => {
    setSuccessMessage(null);
  }, 2000);
};
  const handleCut = (itemsToCut) => { 
  if (!itemsToCut || itemsToCut.length === 0) return; 
  setClipboard({ items: itemsToCut, operation: 'cut' }); 
  setShowContextMenu(false); 
  
  const itemNames = itemsToCut.map(item => item.name).join(', ');
  setSuccessMessage(`✂️ Cut ${itemsToCut.length} item(s) to clipboard`);
  setTimeout(() => {
    setSuccessMessage(null);
  }, 2000);
};
  
const handlePaste = async () => {
    if (!clipboard.items || clipboard.items.length === 0 || !clipboard.operation) {
      console.log('📋 Clipboard is empty or operation not defined.');
      return;
    }
    
    // Check for potential overwrites first
    const filesToOverwrite = clipboard.items.filter(item => 
      files.some(file => file.name === item.name)
    );
    
    if (filesToOverwrite.length > 0) {
      const confirmPaste = await new Promise(resolve => {
        const modal = document.createElement('div');
        modal.className = 'fm-dialog-overlay';
        modal.innerHTML = `
          <div class="fm-dialog">
            <h3>Overwrite Files?</h3>
            <p>The following file(s) already exist in this folder and will be overwritten:</p>
            <ul>
              ${filesToOverwrite.map(f => `<li>${f.name}</li>`).join('')}
            </ul>
            <p>Are you sure you want to continue?</p>
            <div class="fm-dialog-buttons">
              <button class="fm-button-secondary" id="cancelPaste">Cancel</button>
              <button class="fm-button-primary danger" id="confirmPaste">Overwrite</button>
            </div>
          </div>
        `;
        document.body.appendChild(modal);

        document.getElementById('cancelPaste').onclick = () => {
          document.body.removeChild(modal);
          resolve(false);
        };
        document.getElementById('confirmPaste').onclick = () => {
          document.body.removeChild(modal);
          resolve(true);
        };
      });

      if (!confirmPaste) return;
    }
  
    // Show progress for paste operations
    setTotalFilesToUpload(clipboard.items.length);
    setUploadedFilesCount(0);
    setUploadProgress(0);
    setShowUploadProgress(true);
    setCurrentUploadFileName('');
  
    setLoading(true); 
    setError(null);
    let failedOps = 0; 
    let errorMessages = []; 
    const operationName = clipboard.operation === 'copy' ? 'copying' : 'moving';
    const sourcePathsWhenCutting = clipboard.operation === 'cut' ? clipboard.items.map(i => i.path) : [];
  
    console.log(`📋 Starting paste operation: ${operationName} ${clipboard.items.length} item(s)`);
    
    for (let i = 0; i < clipboard.items.length; i++) {
      const sourceItem = clipboard.items[i];
      const sourcePath = sourceItem.path; 
      const itemName = sourcePath.split('/').filter(Boolean).pop();
      const destinationPath = `${currentPath}${itemName}${sourceItem.isDirectory ? '/' : ''}`;
      
      // Update progress with current item name
      setCurrentUploadFileName(`${operationName} ${itemName}...`);
      setUploadedFilesCount(i);
      
      // Calculate progress based on number of items completed
      const progressPerItem = 100 / clipboard.items.length;
      setUploadProgress(Math.round(i * progressPerItem));
      
      if (sourcePath === destinationPath || (clipboard.operation === 'cut' && sourcePath.startsWith(destinationPath))) {
        console.warn(`⚠️ Skipping self-operation or moving a folder into itself: ${itemName}`);
        // Still increment the completed count to reflect progress
        setUploadedFilesCount(i + 1);
        setUploadProgress(Math.round((i + 1) * progressPerItem));
        continue;
      }
      
      try {
        if (clipboard.operation === 'copy') {
          await webdavService.copy(sourcePath, destinationPath, true); // Overwrite set to true
        } else {
          // Pass overwrite=true for move operation to fix the 412 error
          await webdavService.move(sourcePath, destinationPath, true);
        }
        
        console.log(`✅ Successfully ${operationName} ${itemName}`);
        
        // Update progress after completion
        setUploadedFilesCount(i + 1);
        setUploadProgress(Math.round((i + 1) * progressPerItem));
        
      } catch (err) { 
        failedOps++; 
        errorMessages.push(`Failed to ${clipboard.operation} ${itemName}: ${err.message}`); 
        console.error(`❌ Error ${operationName} ${itemName}:`, err);
      }
      
      // Small delay to show progress
      if (i < clipboard.items.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    // Complete progress
    setCurrentUploadFileName(`Operation completed.`); 
    setUploadedFilesCount(clipboard.items.length);
    setUploadProgress(100);
    
    if (clipboard.operation === 'cut') {
      setClipboard({ items: [], operation: null });
    }
  
    // Hide progress after a brief moment
    setTimeout(() => {
      setShowUploadProgress(false);
    }, 1500);
  
    // CRITICAL FIX: Refresh all source folders if it was a cut operation
    if (clipboard.operation === 'cut') {
      const uniqueSourceFolders = [...new Set(sourcePathsWhenCutting.map(p => p.substring(0, p.lastIndexOf('/') + 1)))];
      for (const folder of uniqueSourceFolders) {
        await fetchFiles(folder, true);
      }
    }
    fetchFiles(currentPath, true); 
    
    if (failedOps > 0) {
      alert(`Paste completed with errors: ${errorMessages.join('; ')}`);
      setError(`Paste completed with errors: ${errorMessages.join('; ')}`);
    } else {
      // Do not show a blocking alert for success
      console.log(`Successfully ${clipboard.operation === 'copy' ? 'copied' : 'moved'} ${clipboard.items.length} item(s).`);
    }
    
    setLoading(false);
  };

  const filteredAndSortedFiles = useMemo(() => {
    const userHome = username ? `/home/${username}/` : null;
    const relSegs = (userHome && currentPath.startsWith(userHome))
      ? currentPath.substring(userHome.length).split('/').filter(Boolean)
      : [];
    const relDepth = relSegs.length;
    const currentRelPath = relSegs.join('/'); // e.g. '' | 'domiat' | 'domiat/domiat-1'

    const filtered = files.filter(file => {
      if (!file.name) return false;
      if (!file.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;

      if (!isAdmin) {
        if (file.name === 'menu') return false; // always hide menu folder

        // Use devicePaths (media_files_link paths) for precise filtering
        if (file.isDirectory && devicePaths !== null && devicePaths.length > 0) {
          const filePath = currentRelPath ? `${currentRelPath}/${file.name}` : file.name;

          // Check if we are already inside a device folder (at or below device level)
          const insideDevice = devicePaths.some(p => currentRelPath === p || currentRelPath.startsWith(p + '/'));

          if (insideDevice) {
            // Inside a device folder — show all subfolders freely, no filtering
          } else if (relDepth === 0) {
            // At home root: show folders that ARE a device or CONTAIN devices (branch/org folders)
            const visible = devicePaths.some(p => p === file.name || p.startsWith(`${file.name}/`));
            if (!visible) return false;
          } else {
            // In a branch/org folder above device level: only show folders on the path to a device
            const visible = devicePaths.some(p => p === filePath || p.startsWith(`${filePath}/`));
            if (!visible) return false;
          }
        }
      }
      return true;
    });
    return sortItems(filtered, sortBy, sortDirection);
  }, [files, searchTerm, sortBy, sortDirection, sortItems, isAdmin, username, currentPath, devicePaths]);

  const selectionSummary = useMemo(() => {
    const selected = filteredAndSortedFiles.filter(item => selectedItems.includes(item.path));
    const totalSize = selected.reduce((sum, item) => sum + (item.size || 0), 0); const count = selected.length;
    if (count === 0) return ''; return `${count} item${count > 1 ? 's' : ''} selected (${formatBytes(totalSize)})`;
  }, [selectedItems, filteredAndSortedFiles]);

  const fileSummary = useMemo(() => {
    const totalFolders = files.filter(f => f.isDirectory).length; const totalFilesCount = files.length - totalFolders;
    const totalSize = files.reduce((sum, item) => sum + (item.size || 0), 0);
    return `Total: ${totalFolders} Folder${totalFolders !== 1 ? 's' : ''}, ${totalFilesCount} File${totalFilesCount !== 1 ? 's' : ''} (${formatBytes(totalSize)})`;
  }, [files]);

  const isAllSelected = useMemo(() => {
    if (filteredAndSortedFiles.length === 0) return false;
    return selectedItems.length === filteredAndSortedFiles.length;
  }, [selectedItems, filteredAndSortedFiles]);

  const canPaste = useMemo(() => clipboard.items && clipboard.items.length > 0 && clipboard.operation, [clipboard]);

  const renderLoading = () => <div className="fm-loading"><div className="fm-spinner"></div><p>Loading files...</p></div>;
  const renderError = () => (
    <div className="fm-error">
      <p>{error}</p>
      <button onClick={() => fetchFiles(currentPath)} className="fm-button-secondary">Retry</button>
      {error && typeof error === 'string' && error.toLowerCase().includes('authentication') && <p>Authentication failed. Please try logging out and back in.</p>}
      {error && typeof error === 'string' && error.toLowerCase().includes('permission denied') && <p>You do not have permission to access this folder or perform this action.</p>}
    </div>
  );
  
  const renderSuccess = () => {
    if (!successMessage) return null;
    return (
      <div className="fm-success-message">
        <p>{successMessage}</p>
        <button onClick={() => setSuccessMessage(null)}>OK</button>
      </div>
    );
  };

  const renderListView = () => (
    <div className="fm-list-view">
      <div className="fm-list-header-sticky" ref={listHeaderRef}>
        <table className="fm-list-table header-only">
          <thead>
            <tr>
              <th className="fm-list-header-cell checkbox"><input type="checkbox" checked={isAllSelected} onChange={handleSelectAll} disabled={filteredAndSortedFiles.length === 0} /></th>
              <th className="fm-list-header-cell icon"></th>
              <th className={`fm-list-header-cell name ${sortBy === 'name' ? 'sorted' : ''}`} onClick={() => handleSort('name')}>Name {sortBy === 'name' && <span className="fm-sort-indicator">{sortDirection === 'asc' ? <SortAscIcon /> : <SortDescIcon />}</span>}</th>
              <th className={`fm-list-header-cell size ${sortBy === 'size' ? 'sorted' : ''}`} onClick={() => handleSort('size')}>Size {sortBy === 'size' && <span className="fm-sort-indicator">{sortDirection === 'asc' ? <SortAscIcon /> : <SortDescIcon />}</span>}</th>
{window.innerWidth > 768 && (
  <th className={`fm-list-header-cell modified ${sortBy === 'modified' ? 'sorted' : ''}`} onClick={() => handleSort('modified')}>
    Date Modified
    {sortBy === 'modified' && (
      <span className="fm-sort-indicator">
        {sortDirection === 'asc' ? <SortAscIcon /> : <SortDescIcon />}
      </span>
    )}
  </th>
)}


            </tr>
          </thead>
        </table>
      </div>
      <div className={`fm-list-table-container ${isEmbedded ? 'embedded' : ''}`}>
        <table className="fm-list-table body-only">
          <tbody>
            {filteredAndSortedFiles.map(item => {
              const isCut = clipboard.operation === 'cut' && clipboard.items.some(ci => ci.path === item.path);
              const isDragOver = item.isDirectory && dragOverFolder === item.path;
              return (
                <tr 
                  key={item.path} 
                  data-path={item.path} 
                  className={`fm-list-row ${selectedItems.includes(item.path) ? 'selected' : ''} ${item.isDirectory ? 'is-directory' : ''} ${isCut ? 'cut' : ''} ${isDragOver ? 'drag-over' : ''}`} 
                  onClick={(e) => handleItemClick(item, e)} 
                  onContextMenu={(e) => handleContextMenu(e, item)}
                  draggable={!item.isDirectory}
                  onDragStart={(e) => handleDragStart(e, item)}
                  onDrop={(e) => handleDropOnFolder(e, item)}
                  onDragOver={(e) => handleDragOverFolder(e, item)}
                  onDragLeave={handleDragLeaveFolder}
                >
                  <td className="fm-list-cell checkbox"><input type="checkbox" checked={selectedItems.includes(item.path)} onChange={(e) => handleItemSelect(item.path, e)} /></td>
                  <td className="fm-list-cell icon">{getIconComponent(item, true, 'list')}</td>
                  <td className="fm-list-cell name">{item.name}</td>
                  <td className="fm-list-cell size">{item.isDirectory ? '--' : formatBytes(item.size)}</td>
                  {window.innerWidth > 768 && (
  <td className="fm-list-cell modified">{formatDate(item.modified)}</td>
)}

                </tr>
              );
            })}
            {filteredAndSortedFiles.length === 0 && <tr><td colSpan={5} className="fm-empty-folder-cell">No files found in this folder.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderGridView = () => (
  <div className="fm-grid-view">
    {filteredAndSortedFiles.map(item => {
      const isCut = clipboard.operation === 'cut' && clipboard.items.some(ci => ci.path === item.path);
      const isDragOver = item.isDirectory && dragOverFolder === item.path;
      return (
        <div
          key={item.path}
          data-path={item.path}
          className={`fm-grid-item ${selectedItems.includes(item.path) ? 'selected' : ''} ${item.isDirectory ? 'is-directory' : ''} ${isCut ? 'cut' : ''} ${isDragOver ? 'drag-over' : ''}`}
          onContextMenu={(e) => handleContextMenu(e, item)}
          title={item.name}
        >
          {/* Thumbnail / icon — click opens/navigates */}
          <div
            className="fm-grid-item-icon"
            onClick={(e) => handleItemClick(item, e)}
            draggable={!item.isDirectory}
            onDragStart={(e) => handleDragStart(e, item)}
            onDrop={(e) => handleDropOnFolder(e, item)}
            onDragOver={(e) => handleDragOverFolder(e, item)}
            onDragLeave={handleDragLeaveFolder}
          >
            {getIconComponent(item, true)}
          </div>

          {/* Footer row — click anywhere here to select */}
          <div
            className="fm-grid-item-footer"
            onClick={(e) => { e.stopPropagation(); handleItemSelect(item.path, { target: { checked: !selectedItems.includes(item.path) } }); }}
          >
            <span className={`fm-grid-checkbox ${selectedItems.includes(item.path) ? 'checked' : ''}`} aria-hidden="true" />
            <span className="fm-grid-item-name">{item.name}</span>
          </div>
        </div>
      );
    })}
  </div>
);

  const renderContextMenu = () => {
    if (!showContextMenu) return null;

    // Context menu for an item
    if (contextMenuType === 'item' && contextMenuTarget) {
      // Use stored selection instead of current selectedItems
      const itemsForAction = filteredAndSortedFiles.filter(item => contextMenuSelection.includes(item.path));
      
      console.log('Context Menu - Using stored selection:', contextMenuSelection);
      console.log('Context Menu - Items for action:', itemsForAction);

      const canDownload = itemsForAction.length > 0;
      // Write operations only inside device (media) folders
      const canDelete = canUpload && itemsForAction.length > 0;
      const canRename = canUpload && itemsForAction.length === 1 && !itemsForAction[0].isDirectory;
      const canPreview = itemsForAction.length === 1 && !itemsForAction[0].isDirectory && ['image', 'video', 'audio', 'pdf', 'text', 'document'].includes(getFileType(itemsForAction[0].name));
      const canCopy = canUpload && itemsForAction.length > 0;
      const canCut  = canUpload && itemsForAction.length > 0;
      const showPaste = canUpload && canPaste;

      return (
        <div ref={contextMenuRef} className="fm-context-menu" style={{ top: contextMenuPosition.y, left: contextMenuPosition.x }}>
          {canPreview && (
            <div className="fm-context-menu-item" onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handlePreview(itemsForAction[0]);
              setShowContextMenu(false);
            }}>
              Preview
            </div>
          )}
          {canDownload && (
            <div className="fm-context-menu-item" onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Context Menu - Download action for:', itemsForAction);
              handleDownload(itemsForAction);
              setShowContextMenu(false);
            }}>
              <DownloadIcon /> Download
            </div>
          )}
          {canRename && (
            <div className="fm-context-menu-item" onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setRenameTarget(itemsForAction[0]);
              setNewName(itemsForAction[0].name);
              setShowRenameDialog(true);
              setShowContextMenu(false);
            }}>
              <RenameIcon /> Rename...
            </div>
          )}
          {canCut && (
            <div className="fm-context-menu-item" onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Context Menu - Cut action for:', itemsForAction);
              handleCut(itemsForAction);
              setShowContextMenu(false);
            }}>
              <CutIcon /> Cut
            </div>
          )}
          {canCopy && (
            <div className="fm-context-menu-item" onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Context Menu - Copy action for:', itemsForAction);
              handleCopy(itemsForAction);
              setShowContextMenu(false);
            }}>
              <CopyIcon /> Copy
            </div>
          )}
          {showPaste && (
            <div className="fm-context-menu-item" onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handlePaste();
              setShowContextMenu(false);
            }}>
              <PasteIcon /> Paste
            </div>
          )}
          {canDelete && (
            <div className="fm-context-menu-item danger" onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Context Menu - Delete action for:', itemsForAction);
              handleDelete(itemsForAction.map(i => i.path));
              setShowContextMenu(false);
            }}>
              <DeleteIcon /> Delete...
            </div>
          )}
        </div>
      );
    } 
    // Context menu for empty space
    else if (contextMenuType === 'empty-space') {
      return (
        <div ref={contextMenuRef} className="fm-context-menu" style={{ top: contextMenuPosition.y, left: contextMenuPosition.x }}>
          <div className="fm-context-menu-item" onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleRefresh();
            setShowContextMenu(false);
          }}>
            <RefreshIcon /> Refresh
          </div>
          <div className="fm-context-menu-item" onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSelectAll();
            setShowContextMenu(false);
          }}>
            <SelectAllIcon /> Select All
          </div>
          {canUpload && (
  <div className="fm-context-menu-item" onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowCreateFolderDialog(true);
    setShowContextMenu(false);
  }}>
    <NewFolderIcon /> Create Folder
  </div>
)}
          {canUpload && (
          <div className="fm-context-menu-item" onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleUploadClick();
            setShowContextMenu(false);
          }}>
            <UploadIcon /> Upload File
          </div>
          )}
          {canUpload && (
          <div className="fm-context-menu-item" onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleFolderUploadClick();
            setShowContextMenu(false);
          }}>
            <FolderIcon /> Upload Folder
          </div>
          )}
          {canUpload && canPaste && (
            <div className="fm-context-menu-item" onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handlePaste();
              setShowContextMenu(false);
            }}>
              <PasteIcon /> Paste
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const renderCreateFolderDialog = () => {
    if (!showCreateFolderDialog) return null;
    return (
      <div className="fm-dialog-overlay">
        <div className="fm-dialog">
          <h3>Create New Folder</h3>
          <input type="text" placeholder="Folder name" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} autoFocus onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()} />
          <div className="fm-dialog-buttons">
            <button className="fm-button-secondary" onClick={() => setShowCreateFolderDialog(false)}>Cancel</button>
            <button className="fm-button-primary" onClick={handleCreateFolder} disabled={!newFolderName || loading || newFolderName.includes('/')}>{loading ? 'Creating...' : 'Create'}</button>
          </div>
        </div>
      </div>
    );
  };

  const renderRenameDialog = () => {
    if (!showRenameDialog || !renameTarget) return null;
    return (
      <div className="fm-dialog-overlay">
        <div className="fm-dialog">
          <h3>Rename Item</h3>
          <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus onKeyPress={(e) => e.key === 'Enter' && handleRename()} />
          <div className="fm-dialog-buttons">
            <button className="fm-button-secondary" onClick={() => setShowRenameDialog(false)}>Cancel</button>
            <button className="fm-button-primary" onClick={handleRename} disabled={!newName || loading || newName === renameTarget.name || newName.includes('/')}>{loading ? 'Renaming...' : 'Rename'}</button>
          </div>
        </div>
      </div>
    );
  };

const renderUploadProgress = () => {
  if (!showUploadProgress) return null;
  return (
    <div className="fm-upload-progress">
      <div className="fm-upload-progress-content">
        <h3>Uploading Files...</h3>
        <p>{`File ${uploadedFilesCount + 1} of ${totalFilesToUpload}`}: {currentUploadFileName}</p>
        <div className="fm-progress-bar-container"><div className="fm-progress-bar" style={{ width: `${uploadProgress}%` }}></div></div>
      </div>
    </div>
  );
};

const renderDownloadProgress = () => {
  if (!isDownloading) return null;
  return (
    <div className="fm-upload-progress">
      <div className="fm-upload-progress-content">
        <h3>Downloading...</h3>
        <p>{currentDownloadFileName}</p>
        <div className="fm-progress-bar-container"><div className="fm-progress-bar" style={{ width: `${downloadProgress}%` }}></div></div>
        <p>{downloadProgress}%</p>
      </div>
    </div>
  );
};  
  const renderDragSelectionBox = () => {
    if (!isDraggingSelection) return null;

    const left = Math.min(selectionStartPos.x, selectionEndPos.x);
    const top = Math.min(selectionStartPos.y, selectionEndPos.y);
    const width = Math.abs(selectionStartPos.x - selectionEndPos.x);
    const height = Math.abs(selectionStartPos.y - selectionEndPos.y);

    return (
      <div
        style={{
          position: 'absolute',
          left: left,
          top: top,
          width: width,
          height: height,
          border: '1px solid #1976d2',
          backgroundColor: 'rgba(25, 118, 210, 0.2)',
          zIndex: 100,
          pointerEvents: 'none', // Important for not blocking mouse events
        }}
      />
    );
  };
  
  const renderPreviewModal = () => {
    if (!showPreviewModal || previewItems.length === 0) return null;
    const currentItem = previewItems[currentPreviewIndex]; 
    const itemType = getFileType(currentItem.name);
    const fileExtension = currentItem.name.split('.').pop().toLowerCase();
    
    // Always prefer blob URLs for previews to avoid browser basic-auth prompts
    const authUrl = previewBlobUrl;
    
    return (
      <div className="fm-preview-modal-overlay" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
        <div className="fm-preview-modal">
          {/* Header: filename + close button */}
          <div className="fm-preview-header">
            <h2>{currentItem.name}</h2>
            <button className="fm-preview-close-btn" onClick={handleClosePreviewModal}><CloseIcon /></button>
          </div>
          
          <div className="fm-preview-content" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
            {previewLoading && <div className="fm-spinner"></div>}
            {previewError && <div className="fm-error">{previewError}</div>}
            {!previewLoading && !previewError && (
              <>
                {itemType === 'image' && authUrl && (
                  <img 
                    src={authUrl} 
                    alt={currentItem.name} 
                    className="fm-preview-image"
                  />
                )}
                
                {/* Video files - show thumbnail like App.js */}
                {itemType === 'video' && authUrl && (
                  <div className="fm-video-preview-container">
                    <img src={authUrl} alt={currentItem.name} className="fm-preview-image" />
                    <div className="fm-video-overlay">
                      <div className="fm-video-play-icon">▶️</div>
                      <div className="fm-video-label">VIDEO</div>
                    </div>
                  </div>
                )}
                
                {itemType === 'audio' && authUrl && (
                  <div className="fm-preview-audio-container">
                    <div className="fm-preview-audio-title">{currentItem.name}</div>
                    <audio src={authUrl} controls className="fm-preview-audio">
                      Your browser does not support the audio tag.
                    </audio>
                  </div>
                )}
                
                {fileExtension === 'pdf' && authUrl && (
                  <div className="fm-preview-pdf-container">
                    <iframe src={authUrl} title={currentItem.name} className="fm-preview-pdf">
                      This browser does not support PDFs. Please download the PDF to view it.
                    </iframe>
                  </div>
                )}
                
                {!['image', 'video', 'audio'].includes(itemType) && fileExtension !== 'pdf' && (
                  <div className="fm-preview-fallback">
                    <div className="fm-preview-fallback-icon">
                      {itemType === 'document' && <DocumentIcon />} 
                      {itemType === 'spreadsheet' && <SpreadsheetIcon />}
                      {itemType === 'presentation' && <PresentationIcon />} 
                      {itemType === 'text' && <TextIcon />}
                      {itemType === 'code' && <CodeIcon />}
                      {!['document', 'spreadsheet', 'presentation', 'text', 'code'].includes(itemType) && <FileIcon />}
                    </div>
                    <div className="fm-preview-fallback-info">
                      <h3>{currentItem.name}</h3>
                      <p>Preview not available for this file type.</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          
          {/* Navigation arrows for multiple files */}
          {previewItems.length > 1 && (
            <>
              <button className="fm-preview-nav-btn prev" onClick={handlePreviewPrev}><ChevronLeftIcon /></button>
              <button className="fm-preview-nav-btn next" onClick={handlePreviewNext}><ChevronRightIcon /></button>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderToolbar = () => {
    // Determine if the "Up" button should be disabled
    let upDisabled = loading;
    if (isAdmin) {
        upDisabled = upDisabled || currentPath === '/home/'; // Admin cannot go above /home/ via UI
    } else if (username) {
        const _userHome = `/home/${username}/`;
        if (!currentPath.startsWith(_userHome) || currentPath === _userHome) {
          upDisabled = true; // At or above home root
        } else if (devicePaths !== null && devicePaths.length > 0) {
          const _relPath = currentPath.substring(_userHome.length).split('/').filter(Boolean).join('/');
          // Branch level = second-to-last segment of any multi-segment devicePath
          // e.g. 'sticks/3asher/3asher-v1' → branch prefix = 'sticks/3asher'
          //      'domiat/domiat-1'          → branch prefix = 'domiat'
          const atBranchLevel = devicePaths
            .filter(p => p.includes('/'))
            .some(p => p.split('/').slice(0, -1).join('/') === _relPath);
          if (atBranchLevel) upDisabled = true; // At branch level — can't go higher
        }
    } else {
        upDisabled = true; // No user context, disable up
    }

if (isEmbedded) {
  return (
    <div className="fm-toolbar fm-embedded-toolbar">
      <div className="fm-toolbar-left">
        {/* Refresh Button */}
        <button 
          className="fm-button" 
          onClick={handleRefresh} 
          title="Refresh" 
          disabled={loading}
        >
          <RefreshIcon />
        </button>
        
        {/* Upload Files Button — only inside device folder */}
        {canUpload && (
        <button
          className="fm-button"
          onClick={handleUploadClick}
          title="Upload Files"
          disabled={loading}
        >
          <UploadIcon />
        </button>
        )}
        
        {/* Rename Selected Button */}
        <button 
          className="fm-button" 
          onClick={() => {
            const selected = filteredAndSortedFiles.filter(i => selectedItems.includes(i.path));
            if (selected.length === 1) {
              setRenameTarget(selected[0]);
              setNewName(selected[0].name);
              setShowRenameDialog(true);
            }
          }} 
          title="Rename Selected" 
          disabled={selectedItems.length !== 1 || loading}
        >
          <RenameIcon />
        </button>
        
        {/* Download Selected Button */}
        <button 
          className="fm-button" 
          onClick={() => handleDownload(filteredAndSortedFiles.filter(i => selectedItems.includes(i.path)))} 
          title="Download Selected" 
          disabled={selectedItems.length === 0 || loading}
        >
          <DownloadIcon />
        </button>
        
        {/* Delete Selected Button */}
        <button 
          className="fm-button danger" 
          onClick={handleDeleteSelected} 
          title="Delete Selected" 
          disabled={selectedItems.length === 0 || loading}
        >
          <DeleteIcon />
        </button>
      </div>
      <div className="fm-toolbar-right">
        {/* Empty - can add search or view toggles later if needed */}
      </div>
    </div>
  );
}

    // Full toolbar for non-embedded view
    return (
      <div className="fm-toolbar">
        <div className="fm-toolbar-left">
          <button className="fm-button" onClick={handleUpDirectory} title="Up one level" disabled={upDisabled}><UpArrowIcon /></button>
          <button className="fm-button" onClick={handleRefresh} title="Refresh" disabled={loading}><RefreshIcon /></button>
          {canUpload && (<button className="fm-button" onClick={() => setShowCreateFolderDialog(true)} title="New Folder" disabled={loading}><NewFolderIcon /></button>)}
          <button className="fm-button" onClick={() => handleDownload(filteredAndSortedFiles.filter(i => selectedItems.includes(i.path)))} title="Download Selected" disabled={selectedItems.length === 0 || loading}><DownloadIcon /></button>
          {canUpload && (<button className="fm-button" onClick={handleUploadClick} title="Upload Files" disabled={loading}><UploadIcon /></button>)}
          {canUpload && (<button className="fm-button" onClick={handleFolderUploadClick} title="Upload Folder" disabled={loading}><FolderIcon /></button>)} {/* Assuming FolderIcon represents Upload Folder here */}
          <button className="fm-button" onClick={() => {
  const selected = filteredAndSortedFiles.filter(i => selectedItems.includes(i.path));
  if (selected.length === 1) {
    setRenameTarget(selected[0]);
    setNewName(selected[0].name);
    setShowRenameDialog(true);
  }
}} title="Rename Selected" disabled={selectedItems.length !== 1 || loading || !canUpload}><RenameIcon /></button>
<button className="fm-button" onClick={() => handleCut(filteredAndSortedFiles.filter(i => selectedItems.includes(i.path)))} title="Cut Selected" disabled={selectedItems.length === 0 || loading || !canUpload}><CutIcon /></button>
          <button className="fm-button" onClick={() => handleCopy(filteredAndSortedFiles.filter(i => selectedItems.includes(i.path)))} title="Copy Selected" disabled={selectedItems.length === 0 || loading || !canUpload}><CopyIcon /></button>
          <button className="fm-button" onClick={handlePaste} title="Paste" disabled={!canPaste || loading || !canUpload}><PasteIcon /></button>
          <button className="fm-button danger" onClick={handleDeleteSelected} title="Delete Selected" disabled={selectedItems.length === 0 || loading || !canUpload}><DeleteIcon /></button>
        </div>
        <div className="fm-toolbar-right">
          {/* Add Select All checkbox here for Grid View */}
          {viewMode === 'grid' && (
            <label className="fm-select-all-checkbox-container">
              <input 
                type="checkbox" 
                checked={isAllSelected} 
                onChange={handleSelectAll} 
                disabled={filteredAndSortedFiles.length === 0} 
              />
              <span className="fm-select-all-label">Select All</span>
            </label>
          )}
          <div className="fm-search-container">
            <SearchIcon />
            <input type="text" placeholder="Search..." className="fm-search-input" value={searchTerm} onChange={handleSearchChange} disabled={loading} />
          </div>
          <div className="fm-view-controls">
            <button className={`fm-button ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => handleViewModeChange('grid')} title="Grid View" disabled={loading}><GridIcon /></button>
            <button className={`fm-button ${viewMode === 'list' ? 'active' : ''}`} onClick={() => handleViewModeChange('list')} title="List View" disabled={loading}><ListIcon /></button>
          </div>
        </div>
      </div>
    );
  };


  return (
    <div
      className={`file-manager ${isDragging ? 'dragging' : ''} view-${viewMode} ${isEmbedded ? 'embedded' : ''}`}
      ref={fileManagerRef}
      onMouseDown={handleDragSelectionStart}
      onMouseMove={handleDragSelectionMove}
      onMouseUp={handleDragSelectionEnd}
      onContextMenu={(e) => {
        // Only show empty space context menu if click is not on an item
        if (!e.target.closest('.fm-grid-item') && !e.target.closest('.fm-list-row') && e.target.closest('.fm-content')) {
          handleContextMenu(e, null); // Pass null for empty space
        } else if (!e.target.closest('.fm-grid-item') && !e.target.closest('.fm-list-row')) {
          // Prevent default context menu if click is outside content or on non-item elements
          e.preventDefault();
        }
      }}
      onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop}
    >
      <input
  type="file"
  ref={fileInputRef}
  onChange={handleFileInputChange}
  style={{ display: 'none' }}
  multiple
  accept=".mp4,.avi,.mov,.wmv,.mkv,.webm,.flv,.mpeg,.mpg,.m4v,.3gp,.ogv,.mp3,.wav,.ogg,.aac,.flac,.wma,.m4a,.opus,.jpg,.jpeg,.png,.gif,.bmp,.webp,.svg,.tif,.tiff,.ico,.pdf,.html,.htm,.xls,.xlsx,.csv,.url" // ADDED .url
/>
      <input 
        type="file" 
        ref={folderInputRef} 
        onChange={handleFolderInputChange} 
        style={{ display: 'none' }} 
        webkitdirectory="true" 
        directory="true"
      />
      <div className="fm-header">
        {renderToolbar()} {/* Toolbar now includes the Up Arrow button */}
        {/* Breadcrumbs */}
        <div className="fm-breadcrumbs">
            {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={crumb.path + index}>
                {index > 0 && <span className="fm-breadcrumb-separator"><ChevronRightIcon /></span>}
                <button
                    type="button"
                    className={`fm-breadcrumb-item fm-button-as-link ${loading ? 'disabled' : ''} ${currentPath === crumb.path ? 'active' : ''}`}
                    onClick={() => !loading && currentPath !== crumb.path && handleNavigate(crumb.path)}
                    disabled={loading || currentPath === crumb.path}
                    title={crumb.name}
                >
                    {/* Special case for root "Homes" for admin or "Home" for user */}
                    {(isAdmin && crumb.path === '/home/') || (!isAdmin && username && crumb.path === `/home/${username}/`) ? <HomeIcon /> : null}
                    {crumb.name}
                </button>
                </React.Fragment>
            ))}
        </div>
      </div>
      
      {/* New mouse selection box overlay */}
      {renderDragSelectionBox()}

      <div className={`fm-content ${isEmbedded ? 'fm-embedded-content' : ''}`}>
        {isDragging && <div className="fm-drag-overlay">Drop files/folders here</div>}
        {loading ? renderLoading()
          : error ? renderError()
          : filteredAndSortedFiles.length === 0 ? <div className="fm-empty-folder"><FolderIcon /><p>This folder is empty.</p></div>
          : (viewMode === 'list' && !isEmbedded) ? renderListView() : renderGridView()
        }
      </div>

      {!isEmbedded && (
        <div className="fm-status-bar">
          <span className="fm-status-selection">{selectionSummary}</span>
          <span className="fm-status-total">{fileSummary}</span>
        </div>
      )}

      {renderContextMenu()}
      {renderCreateFolderDialog()}
      {renderRenameDialog()}
      {renderUploadProgress()}
      {renderDownloadProgress()}
      {renderPreviewModal()}
      {renderSuccess()}
    </div>
  );
}

export default FileManager;
